import React from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';

import {
  ChromeSvg,
  EdgeSvg,
  ErrorSvg,
  FirefoxSvg,
  SafariSvg,
} from '@/assets/icons';
import { MainLoaderSkeleton } from '@/components/hocs/suspense/with-suspense';

import { ROUTES } from '../../constants/routes';
import { useAuth } from '../../context/auth-context';

interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  redirectTo = ROUTES.LOGIN,
}) => {
  const {
    isAuthenticated,
    isLoading,
    isError,
    isOnline,
    isKeycloakReady,
    isKeycloakAuthenticated,
    login,
  } = useAuth();
  const location = useLocation();

  /* Detect if we're currently on a Keycloak auth callback URL.
  During this time, Keycloak JS needs to process the hash parameters (code, state, session_state, error, etc.) and update its internal state.
  */

  const isAuthCallbackInProgress =
    typeof window !== 'undefined' &&
    (window.location.hash.includes('code=') ||
      window.location.hash.includes('session_state=') ||
      window.location.hash.includes('error=') ||
      window.location.search.includes('code=') ||
      window.location.search.includes('session_state=') ||
      window.location.search.includes('error='));

  // Wait for Keycloak to initialize before making any decisions
  if (!isKeycloakReady) {
    return <MainLoaderSkeleton />;
  }

  if (!isOnline) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white">
        <div className="mx-auto flex max-w-md flex-col items-center gap-4 px-6 text-center">
          <h2 className="text-xl font-semibold text-foreground-strong">
            You’re offline
          </h2>
          <p className="text-sm text-gray-600">
            Check your internet connection and try again.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  /* Once Keycloak is ready, check if user is authenticated at Keycloak level
  If not authenticated with Keycloak, redirect to login immediately (don't wait for user data)
  But if we're currently processing an auth callback, just wait and let Keycloak finish instead of starting a new login flow.
  */
  if (!isKeycloakAuthenticated && !isAuthCallbackInProgress) {
    // Build the full URL to redirect back to after login
    // Avoid including any hash fragments since Keycloak may temporarily put auth params there
    const currentUrl = `${window.location.origin}${location.pathname}${location.search}`;

    // Redirect to Keycloak login with the current URL as redirect target
    login(currentUrl);

    // Show a brief loading state while Keycloak redirects
    return <MainLoaderSkeleton />;
  }

  // User is authenticated with Keycloak, now wait for user data to load
  if (isLoading) {
    return <MainLoaderSkeleton />;
  }

  // Show error UI only for user profile endpoint errors (not offline)
  if (isError && isOnline) {
    return (
      <div className="z-100 fixed inset-0 mx-auto flex h-screen max-w-full items-center justify-center bg-white bg-cover">
        <div className="flex h-[400px] w-full items-center justify-center">
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <ErrorSvg className="w-full max-w-[534px] max-xs:max-h-[150px]" />
            <div>
              <h4 className="mb-3 text-[16px] font-[600] text-primary max-sm:text-center sm:text-[24px]">
                Something went wrong
              </h4>
              <p className="mx-auto max-w-[525px] text-[12px] text-foreground-body max-sm:text-center sm:text-[14px]">
                Whoops, we've encountered an issue displaying the requested
                info. Please refresh or download the latest version of your
                browser.
              </p>
              <div className="mt-8 flex w-fit items-center gap-3 max-sm:mx-auto">
                <Link
                  target="_blank"
                  to="https://www.google.com/chrome/update/?_gl=1*6btuy2*_up*MQ..&gclid=EAIaIQobChMIx4jXqMXriAMVAYBQBh3T_BCHEAAYASAAEgLRLPD_BwE&gclsrc=aw.ds"
                >
                  <ChromeSvg />
                </Link>
                <Link
                  target="_blank"
                  to="https://www.mozilla.org/en-US/firefox/download/"
                >
                  <FirefoxSvg />
                </Link>
                <Link
                  target="_blank"
                  to="https://support.apple.com/en-us/102665"
                >
                  <SafariSvg />
                </Link>
                <Link
                  target="_blank"
                  to="https://www.microsoft.com/en-us/edge/download?form=MA13FJ"
                >
                  <EdgeSvg />
                </Link>
              </div>
              <button
                onClick={() => window.location.reload()}
                type="button"
                className="mt-8 w-full max-w-[200px] rounded-lg bg-primary py-2 max-sm:mx-auto"
              >
                <p className="text-[16px] font-[700] text-white">Refresh</p>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Final check: ensure we have full authentication (Keycloak + user data)
  if (!isAuthenticated) {
    // This shouldn't normally happen if the above checks are correct,
    // but handle it as a fallback
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  return <>{children}</>;
};
